import React from "react";
import { Tooltip } from "@/components/common/Tooltip";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { SelectCheckbox } from "components/common/SelectCheckbox";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LinkIcon from "@mui/icons-material/Link";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Spinner } from "components/common/Spinner";
import { datasetUsageTotal } from "@/lib/api/datasets";
import { datasetUsageSummary } from "@/components/main/datasets/DatasetUsageNote";

import { DeleteDialogItem } from "./DeleteDialogItem";
import { TooltipButton } from "components/common/TooltipButton";
import { TransferTable, TableSeverityConfig } from "./TransferTypes";

interface TableItemProps {
  table: TransferTable;
  isSelected: boolean;
  onToggleSelection: (e: React.MouseEvent) => void;
  isHighlighted?: boolean;
  showTransferButton?: boolean;
  onTransfer?: () => void;
  isTransferring?: boolean;
  isTransferSuccess?: boolean;
  isTransferError?: boolean;
  isDragging?: boolean;
  severityConfig?: TableSeverityConfig;
  transferTooltip?: string;
}

export const TableItem: React.FC<TableItemProps> = ({
  table,
  isSelected,
  onToggleSelection,
  isHighlighted = false,
  showTransferButton = false,
  onTransfer,
  isTransferring = false,
  isTransferSuccess = false,
  isTransferError = false,
  isDragging: externalIsDragging = false,
  severityConfig,
  transferTooltip = "Transfer table",
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging: internalIsDragging,
  } = useDraggable({
    id: table.id,
    data: {
      table,
    },
  });

  const isDragging = internalIsDragging || externalIsDragging;

  const style = {
    transform: isDragging ? undefined : CSS.Translate.toString(transform),
    opacity: isDragging ? 0.3 : 1,
  };

  const itemStateClasses = isSelected
    ? "bg-list-selected-light hover:bg-list-selected"
    : isHighlighted
      ? "bg-list-selected-light"
      : "";

  return (
    <div ref={setNodeRef} style={style}>
      <DeleteDialogItem className={`group ${itemStateClasses}`}>
        <div
          className="flex items-center gap-2 px-1 w-full cursor-grab active:cursor-grabbing"
          {...listeners}
          {...attributes}
          onClick={onToggleSelection}
        >
          <div className="flex items-center gap-1">
            <div
              className="text-fg-muted-regular hover:text-fg-regular flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => e.stopPropagation()}
            >
              <DragIndicatorRoundedIcon className="icon-sm" />
            </div>
            <div
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              className={`flex items-center justify-center transition-opacity ${
                isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
              }`}
            >
              <SelectCheckbox
                selected={isSelected}
                onToggle={(e) => {
                  e.stopPropagation();
                  onToggleSelection(e);
                }}
                size="sm"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-1 min-w-0">
            <TableChartOutlinedIcon className="icon-sm text-fg-regular" />
            <span className="text-fg-regular cursor-default truncate">
              {table.name}
            </span>
            {table.subtitle && (
              <span className="text-fg-muted text-xs truncate shrink-0">
                {table.subtitle}
              </span>
            )}
          </div>

          {table.externalLinkCount != null && table.externalLinkCount > 0 && (
            <Tooltip
              title={`Linked to ${table.externalLinkCount} table${table.externalLinkCount === 1 ? "" : "s"} in other models. ${table.externalLinkCount === 1 ? "This link" : "These links"} will break when the table is deleted.`}
              placement="top"
            >
              <span
                className="flex items-center gap-0.5 text-fg-muted-regular shrink-0 cursor-help"
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <LinkIcon className="icon-sm" />
                <span className="text-xs">{table.externalLinkCount}</span>
              </span>
            </Tooltip>
          )}

          {datasetUsageTotal(table.datasetUsage) > 0 && (
            <Tooltip
              title={datasetUsageSummary(table.datasetUsage!)}
              placement="top"
            >
              <span
                className="flex items-center gap-0.5 text-fg-muted-regular shrink-0 cursor-help"
                data-testid="table-dataset-usage"
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <LayersOutlinedIcon className="icon-sm" />
                <span className="text-xs">
                  {datasetUsageTotal(table.datasetUsage)}
                </span>
              </span>
            </Tooltip>
          )}

          {showTransferButton && onTransfer && (
            <div
              className={`transition-opacity flex items-center ${
                isTransferring || isTransferSuccess || isTransferError
                  ? "opacity-100"
                  : "opacity-0 group-hover:opacity-100"
              }`}
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {isTransferring ? (
                <Spinner />
              ) : isTransferSuccess ? (
                <CheckCircleOutlineIcon className="icon-sm text-success" />
              ) : isTransferError ? (
                <CancelOutlinedIcon className="icon-sm text-error" />
              ) : (
                <TooltipButton
                  title={transferTooltip}
                  variant="contextual"
                  size="xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    onTransfer();
                  }}
                  disabled={isTransferring}
                >
                  <SendOutlinedIcon />
                </TooltipButton>
              )}
            </div>
          )}

          {severityConfig && (
            <Tooltip title={severityConfig.tooltip} placement="right">
              <InfoOutlinedIcon
                className={`icon-sm ${severityConfig.color} cursor-help`}
              />
            </Tooltip>
          )}
        </div>
      </DeleteDialogItem>
    </div>
  );
};
